import { v7 } from 'uuid';

import { type Entity } from '../apis/ilink.type';
import { type Article, NUM_GRID } from '../apis/masterdata/masterdata.type';
import { NO_SIZE_GRID_ID, SERVICE_GRID_ID } from '../apis/prices';
import { getServicesArticles } from '../components/ProductPage/product';
import { type ServiceWorkshop, type WorkshopIntervention, InterventionType } from '../apis/interventions/intervention.type';
import { getModelsInformation } from '../apis/spid';
import { type ModelInformation } from '../apis/spid.type';
import { availableArticleLifeStages, emptyArticle, findArticleId } from './articleHelper';
import { checkAvailabilityForArticles, getArticles, isServiceNature } from '../apis/masterdata/masterdata';
import { normalizedString } from './helpers';
import { isInterventionDisabled } from './interventionHelper';
import { getArticleIdFromCode } from './referenceCodeHelper';
import { type StoreIdAndTaxesCondition } from '../store/selectors/price';
import { getPogoFromWorkhopId, POGOS } from '../features/intervention/helpers/pogo';

export interface EntitySearch {
  label: string;
  modelCode: number;
  nature: {
    id: number;
    label: string;
  };
  natureCategory: {
    id: number;
    label: string;
  };
}

export const isInsuranceIntervention = (type: InterventionType) => type === InterventionType.insurance;

const notExistingArticleId = 9999999;

const notExistingArticle = (modelCode?: number): Article => ({
  articleId: notExistingArticleId,
  modelCode,
  selected: true,
  pogoId: null,
});

const buildService = (articles: Article[], isInsurance: boolean) =>
  articles.reduce((acc, article) => {
    if ((isInsurance && article.gridValueId !== POGOS.WARRANTY.masterdataId) || (!isInsurance && article.gridValueId !== POGOS.OGEA.masterdataId)) {
      if (article.gridId === SERVICE_GRID_ID) {
        const isPayable = article.gridValueId === POGOS.PAYING.masterdataId;

        acc.push({
          ...notExistingArticle(article.modelCode),
          ...article,
          estimatedTime: undefined,
          selected: isPayable,
          free: !isPayable,
        });
      } else {
        const index = acc.findIndex(findArticleId(article.articleId));
        if (article.gridId === NUM_GRID.GRID_THEORETICAL_TIME && index > -1) {
          acc[index].estimatedTime = Number(article?.gridValueId?.replace(' MN', ''));
        }
      }
    }
    return acc;
  }, [] as Article[]);

export const buildServiceArticles = (articles: Article[], interventionType: InterventionType) => {
  const sortedArticles = [...articles]
    .filter(article => article.available)
    .sort((a, b) => a.articleId - b.articleId || (a.gridId ?? 0) - (b.gridId ?? 0));
  let serviceArticles = buildService(sortedArticles, isInsuranceIntervention(interventionType));

  if (!serviceArticles.length) {
    return [notExistingArticle(articles.at(0)?.modelCode)];
  }

  if ([InterventionType.insurance, InterventionType.homeService].includes(interventionType)) {
    const pogo = isInsuranceIntervention(interventionType) ? POGOS.OGEA.masterdataId : POGOS.WARRANTY.masterdataId;
    const hasType = serviceArticles.some(serviceArticle => serviceArticle.gridValueId === pogo);
    serviceArticles = serviceArticles.map(article => ({
      ...article,
      selected: hasType ? article.gridValueId === pogo : article.selected,
    }));
  }

  if (!serviceArticles.some(article => article.selected)) {
    serviceArticles[0].selected = true;
  }

  return serviceArticles;
};

export const buildSparePartArticles = (articles: Article[]) => {
  let builtArticles: Article[] = [];
  if (articles.length === 0) {
    builtArticles = [emptyArticle()];
  } else if (articles[0].gridId === NO_SIZE_GRID_ID) {
    builtArticles = articles
      .filter((article, index) => index === articles.findIndex(findArticleId(article.articleId)))
      .map(article => ({ ...article, labelGrid: undefined }));
    if (builtArticles.length > 1) {
      builtArticles = builtArticles.map(article => ({ ...article, labelGrid: article.articleId.toString() }));
    }
  } else {
    builtArticles = [...articles]
      .sort((a, b) => a.articleId - b.articleId || (b.gridId ?? 0) - (a.gridId ?? 0))
      .reduce((acc, article) => {
        if (acc.some(findArticleId(article.articleId))) {
          const index = acc.findIndex(articleToCompare => articleToCompare.eanNum === article.eanNum);
          if (index > -1) {
            acc[index].labelGrid = `${acc[index].labelGrid} - ${article.labelGrid}`;
          }
        } else {
          acc.push(article);
        }
        return acc;
      }, [] as Article[]);
  }

  return builtArticles.length === 1 ? builtArticles.map(article => ({ ...article, selected: true })) : builtArticles;
};

export const checkEntitySearch = (entity: Entity, searchedEntity: string) =>
  !searchedEntity ||
  (entity.name && normalizedString(entity.name).includes(normalizedString(searchedEntity))) ||
  entity.modelCode.toString().includes(searchedEntity) ||
  entity.articles?.some(article => article.articleId.toString().includes(searchedEntity));

interface CheckForArticleParams {
  userInputSearch: string;
  handleArticleCode: (code: number) => Promise<void>;
  errorMessageServiceInSparePart: string;
  errorMessageUnavailable: string;
  storeId: number;
  withAlgolia?: boolean;
}

export const checkForArticles = async ({
  userInputSearch,
  handleArticleCode,
  errorMessageServiceInSparePart,
  errorMessageUnavailable,
  storeId,
  withAlgolia = false,
}: CheckForArticleParams): Promise<Entity | null> => {
  if (!userInputSearch) {
    return null;
  }

  const productCode = await getArticleIdFromCode(userInputSearch);
  if (!productCode) {
    return null;
  }

  const fullArticles = await getArticles([productCode]);
  if (fullArticles.length === 0) {
    await handleArticleCode(productCode);
    return null;
  }

  const isServiceCode = await isServiceNature(productCode);
  if (isServiceCode) {
    throw new Error(errorMessageServiceInSparePart);
  }

  const availableArticles = await checkAvailabilityForArticles(
    fullArticles.map(article => article.articleId),
    availableArticleLifeStages,
    storeId
  );
  const articles = fullArticles.filter(article =>
    availableArticles.some(availableArticle => availableArticle.articleId === article.articleId && availableArticle.available)
  );

  if (articles.length > 1) {
    const sortedArticles = articles
      .map(article => ({ ...article, pogoId: null }))
      .sort(({ labelGrid: labelGridA = '' }, { labelGrid: labelGridB = '' }) =>
        labelGridA.localeCompare(labelGridB, undefined, { numeric: true, sensitivity: 'base' })
      );

    if (withAlgolia) {
      return {
        modelCode: productCode,
        name: '',
        quantity: 0,
        articles: sortedArticles,
      };
    }

    try {
      const [modelInformations] = await getModelsInformation([productCode]);
      return {
        modelCode: modelInformations.modelCode,
        name: modelInformations.label,
        imageUrl: modelInformations.imageUrl,
        quantity: 0,
        articles: sortedArticles,
      };
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Unknown error');
    }
  }
  if (articles.length === 1) {
    await handleArticleCode(articles[0].articleId);
    return null;
  }

  throw new Error(errorMessageUnavailable);
};

interface ServicePreparationData {
  modelsInformation: Pick<ModelInformation, 'modelCode' | 'label' | 'imageUrl'>[];
  articles: Article[];
}

export async function getServicePreparationData({
  services = [],
  stateId,
  interventionType,
  priceOptions,
  withAlgolia = false,
}: Partial<WorkshopIntervention> & { priceOptions: StoreIdAndTaxesCondition; withAlgolia?: boolean }): Promise<ServicePreparationData> {
  const servicesModelCodes = services.map(service => service.modelCode);
  const modelsInformation = withAlgolia
    ? []
    : (await getModelsInformation(servicesModelCodes)).map(({ modelCode, label, imageUrl }) => ({ modelCode, label, imageUrl }));
  const articles = stateId && isInterventionDisabled(stateId) ? [] : await getServicesArticles(servicesModelCodes, priceOptions, interventionType);

  return { modelsInformation, articles };
}

export const buildServicesFromIntervention = (
  services: ServiceWorkshop[],
  { modelsInformation, articles: allArticles }: ServicePreparationData,
  stateId: number
): Entity[] => {
  if (isInterventionDisabled(stateId)) {
    return services.map(service => {
      const serviceModel = modelsInformation.find(model => model.modelCode === service.modelCode);
      return {
        id: v7(),
        name: serviceModel?.label ?? service.label,
        modelCode: service.modelCode,
        quantity: service.quantity,
        imageUrl: serviceModel?.imageUrl ?? '',
        articles: [
          {
            pogoId: service.pogoId,
            articleId: service.itemCode,
            free: service.pogoId !== POGOS.PAYING.workshopId,
            labelGrid: getPogoFromWorkhopId(service.pogoId)?.masterdataId,
            estimatedTime: service.theoreticalTime,
            gridValueId: getPogoFromWorkhopId(service.pogoId)?.masterdataId,
            selected: true,
            available: true,
          },
        ],
      };
    });
  }

  return services.map(service => {
    const serviceModel = modelsInformation.find(image => image.modelCode === service.modelCode);
    const articles = [...allArticles.filter(model => model.modelCode === service.modelCode)];

    if (!articles.some(findArticleId(service.itemCode))) {
      articles.push({
        articleId: service.itemCode,
        free: service.pogoId !== POGOS.PAYING.workshopId,
        labelGrid: service.pogo.toUpperCase(),
        estimatedTime: service.theoreticalTime,
        gridValueId: getPogoFromWorkhopId(service.pogoId)?.masterdataId,
        selected: true,
        disabled: true,
        modelCode: service.modelCode,
        pogoId: service.pogoId,
      });
    }

    return {
      id: v7(),
      name: serviceModel?.label ?? service.label,
      modelCode: service.modelCode,
      quantity: service.quantity,
      imageUrl: serviceModel?.imageUrl,
      articles: articles.map(article => ({
        ...article,
        estimatedTime: service.theoreticalTime,
        selected: article.articleId === service.itemCode,
        pogoId: article.articleId === service.itemCode ? service.pogoId : article.pogoId,
        free: article.articleId === service.itemCode ? service.pogoId !== POGOS.PAYING.workshopId : article.free,
      })),
    };
  });
};
